import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { HUBS } from "@/data/care";

const NAV = [
  { to: "/about", label: "About" },
  { to: "/why-choose", label: "Why Dr Sidhu" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/nri", label: "For NRI" },
  { to: "/book", label: "Book" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [careOpen, setCareOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setCareOpen(false); }, [pathname]);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background,backdrop-filter,border] duration-500 ${
          scrolled ? "bg-bone/75 backdrop-blur-2xl hairline-b" : "bg-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-3">
            <span className="font-serif text-[18px] md:text-[20px] tracking-tightest text-ink">Dr Tegbir</span>
            <span className="hidden md:inline kbd">Vascular · IR</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {/* Care dropdown */}
            <div className="relative" onMouseEnter={() => setCareOpen(true)} onMouseLeave={() => setCareOpen(false)}>
              <NavLink
                to="/care"
                data-testid="nav-link-care"
                className={({ isActive }) =>
                  `text-[13px] tracking-wide inline-flex items-center gap-1 transition-colors ${isActive || pathname.startsWith("/care") || pathname.startsWith("/conditions") ? "text-ink" : "text-ink-muted hover:text-ink"}`
                }
              >
                Care <ChevronDown size={12} className={`transition-transform ${careOpen ? "rotate-180" : ""}`}/>
              </NavLink>
              <AnimatePresence>
                {careOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[560px] bg-bone hairline p-5 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.25)]"
                    data-testid="nav-care-dropdown"
                  >
                    <div className="kbd mb-3">— Clinical hubs</div>
                    <div className="grid grid-cols-2 gap-1">
                      {HUBS.map((h) => (
                        <Link
                          key={h.slug}
                          to={`/care/${h.slug}`}
                          data-testid={`nav-hub-${h.slug}`}
                          className="group p-3 rounded-sm hover:bg-white transition-colors"
                        >
                          <div className="text-xs text-ink-muted group-hover:text-crimson">{h.kicker}</div>
                          <div className="mt-1 font-serif text-[15px] tracking-tightest leading-tight">{h.name}</div>
                        </Link>
                      ))}
                    </div>
                    <div className="mt-4 pt-3 border-t border-black/10 flex items-center justify-between">
                      <Link to="/care" className="kbd hover:text-ink">All care →</Link>
                      <span className="kbd text-ink-muted">34 conditions</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `text-[13px] tracking-wide transition-colors ${isActive ? "text-ink" : "text-ink-muted hover:text-ink"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/urgent" data-testid="nav-urgent-cta" className="hidden md:inline-flex btn-crimson">
              Urgent evaluation
            </Link>
            <button
              data-testid="nav-menu-toggle"
              className="md:hidden p-2 -mr-2"
              onClick={() => setOpen((v) => !v)}
              aria-label="Menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-bone pt-20 px-6 md:hidden overflow-y-auto"
          >
            <div className="flex flex-col gap-6 py-8">
              <Link to="/care" className="font-serif text-4xl tracking-tightest">Care</Link>
              <div className="pl-4 flex flex-col gap-2 text-sm text-ink-muted">
                {HUBS.map((h) => (
                  <Link key={h.slug} to={`/care/${h.slug}`}>{h.name}</Link>
                ))}
              </div>
              {NAV.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link to={n.to} className="font-serif text-4xl tracking-tightest">
                    {n.label}
                  </Link>
                </motion.div>
              ))}
              <Link to="/urgent" className="btn-crimson mt-6 self-start">Urgent evaluation</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
